import { IconKey, IconTrash } from "@tabler/icons-react";
import type { ColumnDef } from "@tanstack/react-table";
import { Button, Flex, Space, Switch, Tooltip, Typography } from "antd";
import { formatDistanceToNow } from "date-fns";
import { useAgents } from "@/common/artemis-operations/agents";
import { useRouteOrgSlug } from "@/common/artemis-operations/organization";
import {
	useDeleteWebhook,
	useRotateWebhookSecret,
	useUpdateWebhook,
	useWebhook,
	useWebhookDeliveries,
	webhookUrl,
} from "@/common/artemis-operations/webhooks";
import {
	type ArtemisWebhookDelivery,
	WebhookAction,
	WebhookStatus,
} from "@/common/model";
import { useNotify } from "@/common/use-notify";
import {
	BaseContainer,
	COPYABLE,
	CustomIcon,
	DateField,
	IdField,
	QueryGuard,
} from "@/components/base";
import { BaseDetails } from "@/components/base-details";
import { useFrictionConfirm } from "@/components/friction-confirm-dialog";
import { CustomDrawer } from "@/components/layout/workspace/custom-drawer";
import { ResponsiveTable } from "@/components/responsive-table";
import {
	WebhookActionLabelMap,
	WebhookDeliveryStatusBadge,
} from "./webhook-badges";

interface WebhookDrawerProps {
	webhookId: string | null;
	workspaceId: string;
	onClose: () => void;
	/** Called with a freshly rotated secret, which the caller must show once. */
	onRotated: (issued: { webhookId: string; secret: string }) => void;
}

/** A webhook's settings and delivery history, with enable/disable, secret rotation and delete. */
export function WebhookDrawer({
	webhookId,
	workspaceId,
	onClose,
	onRotated,
}: WebhookDrawerProps) {
	const notify = useNotify();
	const { data: webhook, isPending, isError } = useWebhook(webhookId ?? "");
	const deliveries = useWebhookDeliveries(webhookId ?? "");
	const { data: agents } = useAgents(workspaceId);
	const updateWebhook = useUpdateWebhook(webhookId ?? "");
	const rotateSecret = useRotateWebhookSecret(webhookId ?? "");
	const deleteWebhook = useDeleteWebhook();
	const { confirm, dialog } = useFrictionConfirm();
	const orgSlug = useRouteOrgSlug();

	const taskLink = (d: ArtemisWebhookDelivery) =>
		d.taskId
			? `/${orgSlug}/workspaces/${workspaceId}/tasks/${d.taskId}`
			: undefined;

	const deliveryColumns: ColumnDef<ArtemisWebhookDelivery>[] = [
		{
			accessorKey: "status",
			header: "Status",
			size: 120,
			cell: ({ row }) => (
				<WebhookDeliveryStatusBadge status={row.original.status} />
			),
		},
		{
			accessorKey: "receivedAt",
			header: "Received",
			size: 180,
			cell: ({ row }) => (
				<DateField value={row.original.receivedAt as string} />
			),
		},
		{
			accessorKey: "taskId",
			header: "Task",
			cell: ({ row }) => (
				<IdField
					id={row.original.taskId ?? undefined}
					link={taskLink(row.original)}
				/>
			),
		},
		{
			accessorKey: "error",
			header: "Error",
			cell: ({ row }) => (
				<Typography.Text
					type="danger"
					ellipsis={{ tooltip: row.original.error }}
				>
					{row.original.error ?? "-"}
				</Typography.Text>
			),
		},
	];

	if (!webhookId) return null;

	const rotate = () =>
		confirm({
			title: "Rotate secret",
			message:
				"The current secret stops working immediately. Every caller must be updated with the new one.",
			confirmText: "rotate",
			confirmButtonLabel: "Rotate",
			onConfirm: () =>
				rotateSecret.mutate(undefined, {
					onSuccess: (data) =>
						onRotated({
							webhookId,
							secret: data.rotateWebhookSecret.secret,
						}),
					onError: (err) =>
						notify.error(
							(err as Error).message || "Failed to rotate the secret.",
						),
				}),
		});

	const remove = () =>
		confirm({
			message: "The webhook's URL stops working immediately.",
			onConfirm: () =>
				deleteWebhook.mutate(webhookId, {
					onSuccess: () => {
						notify.success("Webhook deleted.");
						onClose();
					},
					onError: (err) =>
						notify.error(
							(err as Error).message || "Failed to delete the webhook.",
						),
				}),
		});

	const setEnabled = (enabled: boolean) =>
		updateWebhook.mutate(
			{ status: enabled ? WebhookStatus.ENABLED : WebhookStatus.DISABLED },
			{
				onError: (err) =>
					notify.error(
						(err as Error).message || "Failed to update the webhook.",
					),
			},
		);

	return (
		<CustomDrawer
			title={webhook?.name ?? "Webhook"}
			open={!!webhookId}
			onClose={onClose}
			size="large"
			extra={
				<Space>
					<Tooltip title="Rotate secret">
						<Button
							type="text"
							icon={<CustomIcon icon={IconKey} />}
							onClick={rotate}
						/>
					</Tooltip>
					<Tooltip title="Delete webhook">
						<Button
							danger
							type="text"
							icon={<CustomIcon icon={IconTrash} />}
							onClick={remove}
						/>
					</Tooltip>
				</Space>
			}
		>
			<Space vertical style={{ width: "100%" }} size="large">
				<QueryGuard isPending={isPending} isError={isError} isNull={!webhook}>
					{webhook && (
						<BaseDetails
							item={webhook}
							fields={[
								{
									key: "id",
									label: "URL",
									render: (id) => (
										<Typography.Text
											copyable={{ ...COPYABLE, text: webhookUrl(id) }}
											ellipsis={{ tooltip: webhookUrl(id) }}
										>
											{webhookUrl(id)}
										</Typography.Text>
									),
								},
								{
									key: "status",
									label: "Enabled",
									render: (status) => (
										<Switch
											size="small"
											checked={status === WebhookStatus.ENABLED}
											loading={updateWebhook.isPending}
											onChange={setEnabled}
											aria-label="Enable webhook"
										/>
									),
								},
								{
									key: "action",
									label: "Does",
									render: (action) => WebhookActionLabelMap[action],
								},
								{
									key: "agentId",
									label: "Default agent",
									hidden: (_, w) => w.action !== WebhookAction.CREATE_TASK,
									render: (agentId) =>
										agentId
											? (agents?.find((a) => a?.id === agentId)?.name ??
												agentId)
											: "-",
								},
								{
									key: "description",
									label: "Default description",
									hidden: (_, w) => w.action !== WebhookAction.CREATE_TASK,
								},
								{
									key: "instructions",
									label: "Default instructions",
									hidden: (_, w) => w.action !== WebhookAction.CREATE_TASK,
									render: (instructions) => (
										<Typography.Paragraph
											ellipsis={{ rows: 3, expandable: true }}
											style={{ marginBottom: 0 }}
										>
											{instructions ?? "-"}
										</Typography.Paragraph>
									),
								},
								{
									key: "projectId",
									label: "Default project",
									render: (id) => <IdField id={id ?? undefined} />,
								},
								{
									key: "deploymentId",
									label: "Default deployment",
									render: (id) => <IdField id={id ?? undefined} />,
								},
								{
									key: "secretRotatedAt",
									label: "Secret issued",
									render: (at) => <DateField value={at as string} />,
								},
								{
									key: "lastInvokedAt",
									label: "Last called",
									render: (at) => <DateField value={at as string | null} />,
								},
							]}
						/>
					)}
				</QueryGuard>

				<BaseContainer size="small" title="Deliveries">
					<QueryGuard
						isPending={deliveries.loading}
						isError={deliveries.isError}
					>
						<ResponsiveTable<ArtemisWebhookDelivery>
							columns={deliveryColumns}
							data={deliveries.rows}
							loading={deliveries.isFetching}
							totalCount={deliveries.totalCount}
							hasNextPage={deliveries.hasNextPage}
							hasPreviousPage={deliveries.hasPreviousPage}
							onNextPage={deliveries.onNextPage}
							onPreviousPage={deliveries.onPreviousPage}
							pageIndex={deliveries.pageIndex}
							pageSize={deliveries.pageSize}
							compactRender={(d) => (
								<Flex justify="space-between" align="center" gap={8}>
									<Space vertical size={2} style={{ minWidth: 0 }}>
										<IdField id={d.taskId ?? undefined} link={taskLink(d)} />
										<Typography.Text type="secondary" style={{ fontSize: 12 }}>
											{formatDistanceToNow(new Date(d.receivedAt as string), {
												addSuffix: true,
											})}
										</Typography.Text>
									</Space>
									<WebhookDeliveryStatusBadge status={d.status} />
								</Flex>
							)}
						/>
					</QueryGuard>
				</BaseContainer>
			</Space>
			{dialog}
		</CustomDrawer>
	);
}
